import React, { useState, useMemo, useEffect } from "react";
import { ArrowUp, ArrowDown } from "lucide-react";
import { constants } from "../utils/ranges";
import { useLocation, useNavigate } from "react-router-dom";

const BMIResultEdit = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const [error, setError] = useState("");
  const [age, setAge] = useState(24);
  const [gender, setGender] = useState("male");
  const [height, setHeight] = useState(175);
  const [weight, setWeight] = useState(75);

  const bmiInfo = useMemo(() => {
    const calcBmi = (weight / (height / 100) ** 2).toFixed(1);
    const selectedBMIRange = constants.BMI.ranges.reduce(
      (p, c) => (calcBmi > c.point ? c : p),
      0
    );
    return {
      value: calcBmi,
      label: selectedBMIRange.label,
      color: selectedBMIRange.color,
    };
  }, [height, weight]);

  // Increment/Decrement helpers
  const handleIncrement = (setter, value, step = 1, max = 300) =>
    setter(Math.min(value + step, max));
  const handleDecrement = (setter, value, step = 1, min = 0) =>
    setter(Math.max(value - step, min));

  useEffect(() => {
    if (typeof state?.age === "number" && state.age > 18 && state.age < 110)
      setAge(state.age);
    if (
      typeof state?.gender === "string" &&
      /^(male|female|others)$/.test(state.gender)
    )
      setGender(state.gender);
    if (
      typeof state?.height === "number" &&
      state.height > 30 &&
      state.height < 250
    )
      setHeight(state.height);
    if (
      typeof state?.weight === "number" &&
      state.weight > 20 &&
      state.weight < 220
    )
      setWeight(state.weight);
  }, [state]);

  const continueToAssessments = () => {
    if (weight < 20 || weight > 220)
      setError("Weight must be between 20 and 220 kgs.");
    else if (height < 30 || height > 250)
      setError("Height must be between 30 and 250 cms.");
    else if (age < 18 || age > 110)
      setError("Age must be between 18 and 110 years.");
    else if (!/^(male|female|others)$/.test(gender))
      setError("Please select a valid gender.");
    else {
      setError("");
      let dob = new Date();
      dob.setFullYear(dob.getFullYear() - age);
      dob = dob.toISOString().split("T")[0];
      const userData = { dob, age, gender, height, weight };
      localStorage.setItem("event-user-data", JSON.stringify(userData));
      navigate("/assessments");
    }
  };

  return (
    <div className="flex flex-col items-center justify-center bg-primary text-white px-6 tab-screen">
      {/* Header */}
      <h1 className="text-4xl font-bold mb-2 text-center">Health Assessment</h1>
      <p className="text-gray-400 mb-6 text-center">
        Take a photo to get instant health metric estimates
      </p>

      {/* Card */}
      <div className="bg-[#181818]/80 backdrop-blur-xl border border-gray-700/50 p-6 rounded-2xl shadow-2xl w-full max-w-2xl transition-all">
        <h2 className="mb-4 text-xl font-semibold text-gray-100">
          Analysis Results
        </h2>

        {/* Inputs */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
          {/* Age */}
          <div>
            <label className="text-gray-400 text-sm mb-1 block">
              Age (years)
            </label>
            <input
              type="number"
              value={age}
              onChange={(e) => setAge(Number(e.target.value))}
              className="w-full bg-[#181818] border border-gray-700 rounded-md px-3 py-2 text-white focus:outline-none focus:border-blue-400 transition"
            />
          </div>

          {/* Gender */}
          <div>
            <label className="text-gray-400 text-sm mb-1 block">Gender</label>
            <select
              value={gender}
              onChange={(e) => setGender(e.target.value)}
              className="w-full bg-[#181818] border border-gray-700 rounded-md px-3 py-2 text-white focus:outline-none focus:border-blue-400 transition"
            >
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="others">Others</option>
            </select>
          </div>

          {/* Height */}
          <div>
            <label className="text-gray-400 text-sm mb-1 block">
              Height (cm)
            </label>
            <div className="flex items-center justify-between bg-[#181818] border border-gray-700 rounded-md">
              <button
                onClick={() => handleDecrement(setHeight, height, 1, 100)}
                className="px-3 py-2 hover:bg-gray-700 rounded-l-md transition"
              >
                <ArrowDown size={18} />
              </button>
              <input
                type="number"
                value={height}
                onChange={(e) => setHeight(Number(e.target.value))}
                className="w-full text-center bg-transparent text-white focus:outline-none"
              />
              <button
                onClick={() => handleIncrement(setHeight, height, 1, 250)}
                className="px-3 py-2 hover:bg-gray-700 rounded-r-md transition"
              >
                <ArrowUp size={18} />
              </button>
            </div>
          </div>

          {/* Weight */}
          <div>
            <label className="text-gray-400 text-sm mb-1 block">
              Weight (kg)
            </label>
            <div className="flex items-center justify-between bg-[#181818] border border-gray-700 rounded-md">
              <button
                onClick={() => handleDecrement(setWeight, weight, 1, 10)}
                className="px-3 py-2 hover:bg-gray-700 rounded-l-md transition"
              >
                <ArrowDown size={18} />
              </button>
              <input
                type="number"
                value={weight}
                onChange={(e) => setWeight(Number(e.target.value))}
                className="w-full text-center bg-transparent text-white focus:outline-none"
              />
              <button
                onClick={() => handleIncrement(setWeight, weight, 1, 300)}
                className="px-3 py-2 hover:bg-gray-700 rounded-r-md transition"
              >
                <ArrowUp size={18} />
              </button>
            </div>
          </div>
        </div>

        {error.length > 0 && (
          <p className="text-red-500 text-sm mb-4 text-center">{error}</p>
        )}

        {/* BMI Display */}
        <div className="bg-[#181818] rounded-xl p-4 text-center shadow-inner">
          <p className="text-white text-sm lg:text-xl md:text-xl mb-3">
            Body Mass Index (BMI)
          </p>
          <h3
            className="text-5xl font-bold mb-2"
            style={{ color: bmiInfo.color }}
          >
            {bmiInfo.value}
          </h3>
          <p className="text-white flex items-center justify-center gap-4">
            {bmiInfo.label}
          </p>

          {/* BMI Range Bar */}
          <div className="mt-6">
            <div className="relative h-2 w-full rounded-full bg-gradient-to-r from-blue-400 via-yellow-300 to-red-500">
              <div
                className="absolute top-[-4px] w-4 h-4 bg-white rounded-full shadow-lg"
                style={{
                  left: `${Math.min((bmiInfo.value / 40) * 100, 100)}%`,
                  transform: "translateX(-50%)",
                }}
              />
            </div>
            <div className="flex justify-between text-xs text-gray-400 mt-2">
              <span>&lt;18.5</span>
              <span>18.5-25</span>
              <span>25-30</span>
              <span>&gt;30</span>
            </div>
          </div>
        </div>
      </div>
      <button
        type="button"
        onClick={continueToAssessments}
        className="mt-8 px-8 w-full py-3 bg-white text-center rounded-full text-primary text-lg font-medium shadow-md hover:shadow-lg transition xl:w-8/12"
      >
        Continue
      </button>
    </div>
  );
};

export default BMIResultEdit;
